/**
 * Séquence de saut entre zones (porteur ou chasseur équipé d'un module de saut), purement visuelle :
 * - départ : un portail s'ouvre devant le vaisseau, qui accélère, s'étire et s'y engouffre ; le portail se referme ;
 * - arrivée : un portail s'ouvre derrière la position d'arrivée, le vaisseau en jaillit et ralentit jusqu'à elle.
 * Pendant la séquence, la simulation est suspendue et une caméra extérieure fixe suit le vaisseau. Le sujet est un
 * nœud du rendu (racine du porteur, vue du chasseur) déplacé temporairement puis rendu à sa pose.
 */
import {
  Color3,
  Constants,
  DynamicTexture,
  FresnelParameters,
  MeshBuilder,
  Quaternion,
  StandardMaterial,
  TargetCamera,
  TransformNode,
  Vector3,
  type Mesh,
  type Scene,
} from '@babylonjs/core';
import type { Vfx } from './vfx';

export interface JumpProfile {
  portalRadius: number;
  /** Départ : distance du portail devant le sujet ; arrivée : derrière sa position finale. */
  portalDistance: number;
  openTime: number;
  travelTime: number;
  closeTime: number;
  /** Étirement maximal le long de l'axe (1 = aucun). */
  stretch: number;
  /** Caméra : décalage depuis la pose de référence (droite, haut, avant), et champ de vision. */
  camera: { right: number; up: number; forward: number; fov: number };
  /** Longueur du sujet (gaine de plasma de la traversée d'atmosphère). */
  length?: number;
}

/** Porteur : grand portail loin devant la proue, séquence lente. Valeurs ajustées à la taille du porteur. */
export function carrierJumpProfile(length: number, radius: number): JumpProfile {
  return {
    portalRadius: radius * 1.35,
    portalDistance: length * 0.5 + 300,
    openTime: 1.3,
    travelTime: 3.4,
    closeTime: 0.7,
    stretch: 1.8,
    camera: { right: length * 1.15, up: length * 0.3, forward: -length * 0.2, fov: 0.95 },
  };
}

/**
 * Porteur à travers une atmosphère (descente vers une surface, remontée en orbite) : pas de portail ; le porteur
 * plonge (ou s'élève) sur `portalDistance` mètres en `travelTime` secondes, gaine de plasma de rayon `portalRadius`.
 */
export function carrierAtmosphereProfile(length: number, radius: number): JumpProfile {
  return {
    portalRadius: radius,
    portalDistance: length * 1.6 + 500,
    openTime: 0,
    travelTime: 4.2,
    closeTime: 0,
    stretch: 1,
    camera: { right: length * 1.0, up: length * 0.35, forward: -length * 0.6, fov: 0.95 },
    length,
  };
}

export const FIGHTER_JUMP: JumpProfile = {
  portalRadius: 15,
  portalDistance: 150,
  openTime: 0.7,
  travelTime: 1.5,
  closeTime: 0.45,
  stretch: 2.6,
  camera: { right: 26, up: 8, forward: -22, fov: 1.0 },
};

const PORTAL_COLOR: [number, number, number] = [0.45, 0.72, 1];
const ease = {
  in: (u: number) => u * u * u,
  out: (u: number) => 1 - (1 - u) ** 3,
  /** Ouverture avec léger dépassement. */
  back: (u: number) => 1 + 2.2 * (u - 1) ** 3 + 1.2 * (u - 1) ** 2,
};
const clamp01 = (v: number) => Math.max(0, Math.min(1, v));

interface Portal {
  root: TransformNode;
  disc: Mesh;
  age: number;
  /** Instant de début de fermeture (Infinity tant qu'il reste ouvert). */
  closeAt: number;
  profile: JumpProfile;
}

/** Traversée d'atmosphère en cours. */
interface AtmosphereRun {
  kind: 'depart' | 'arrive';
  subject: TransformNode;
  profile: JumpProfile;
  /** Départ : pose initiale ; arrivée : pose finale. */
  origin: Vector3;
  /** Sens du déplacement (monde). */
  dir: Vector3;
  plasma: Mesh;
  t: number;
  sparks: number;
  burst: boolean;
  done: () => void;
}

interface Run {
  kind: 'depart' | 'arrive';
  subject: TransformNode;
  profile: JumpProfile;
  /** Pose de référence : départ = pose initiale ; arrivée = pose finale. */
  origin: Vector3;
  rotation: Quaternion;
  forward: Vector3;
  portal: Portal;
  t: number;
  /** Le sujet a franchi le portail (départ) ou en est sorti (arrivée). */
  crossed: boolean;
  done: () => void;
  ended: boolean;
}

export class JumpSequence {
  readonly camera: TargetCamera;
  private run: Run | null = null;
  private atmo: AtmosphereRun | null = null;
  private plasmaMaterial: StandardMaterial | null = null;
  private readonly portals: Portal[] = [];
  private readonly swirl: DynamicTexture;
  private readonly ringMaterial: StandardMaterial;
  private readonly discMaterial: StandardMaterial;
  private speed = 1;
  private lookAt = new Vector3();
  /** Repères sonores : ouverture d'un portail, passage du vaisseau. */
  onCue: ((cue: 'open' | 'cross') => void) | null = null;

  constructor(private readonly scene: Scene, private readonly vfx: Vfx) {
    this.camera = new TargetCamera('jump-camera', new Vector3(0, 0, 0), scene);
    this.camera.minZ = 0.5;
    this.camera.maxZ = 60000;
    this.swirl = makeSwirlTexture(scene);
    this.ringMaterial = new StandardMaterial('portal-ring', scene);
    this.ringMaterial.disableLighting = true;
    this.ringMaterial.emissiveColor = new Color3(...PORTAL_COLOR);
    this.discMaterial = new StandardMaterial('portal-disc', scene);
    this.discMaterial.disableLighting = true;
    this.discMaterial.emissiveTexture = this.swirl;
    this.discMaterial.opacityTexture = this.swirl;
    this.discMaterial.emissiveColor = Color3.White();
    this.discMaterial.alphaMode = Constants.ALPHA_ADD;
    this.discMaterial.disableDepthWrite = true;
    this.discMaterial.backFaceCulling = false;
  }

  get active(): boolean {
    return this.run !== null || this.atmo !== null;
  }

  /** Descente (ou remontée) : le sujet part de sa pose actuelle selon `dir`, gaine de plasma croissante. */
  descend(subject: TransformNode, profile: JumpProfile, dir: Vector3, done: () => void): void {
    this.startAtmosphere({ kind: 'depart', subject, profile, origin: subject.position.clone(), dir: dir.normalizeToNew(), plasma: this.makePlasma(profile), t: 0, sparks: 0, burst: false, done });
  }

  /** Arrivée par l'atmosphère : le sujet arrive selon `dir` jusqu'à sa pose actuelle, le plasma s'éteint. */
  emerge(subject: TransformNode, profile: JumpProfile, dir: Vector3, done: () => void): void {
    this.startAtmosphere({ kind: 'arrive', subject, profile, origin: subject.position.clone(), dir: dir.normalizeToNew(), plasma: this.makePlasma(profile), t: 0, sparks: 0, burst: false, done });
  }

  private makePlasma(profile: JumpProfile): Mesh {
    if (!this.plasmaMaterial) {
      const m = new StandardMaterial('atmosphere-plasma', this.scene);
      m.disableLighting = true;
      m.emissiveColor = new Color3(1, 0.42, 0.12);
      m.alpha = 0.99;
      m.alphaMode = Constants.ALPHA_ADD;
      m.disableDepthWrite = true;
      m.backFaceCulling = false;
      m.opacityFresnelParameters = new FresnelParameters({ bias: 0.15, power: 1.6, leftColor: Color3.White(), rightColor: new Color3(0.25, 0.25, 0.25) });
      this.plasmaMaterial = m;
    }
    // Enveloppe autour de toute la coque (repère du sujet), bords lumineux (Fresnel).
    const mesh = MeshBuilder.CreateSphere('atmosphere-plasma', { diameter: 2, segments: 32 }, this.scene);
    mesh.material = this.plasmaMaterial;
    mesh.isPickable = false;
    mesh.applyFog = false;
    const r = profile.portalRadius;
    mesh.scaling.set(r * 1.35, r * 1.35, Math.max(r * 1.35, (profile.length ?? r * 2) * 0.62));
    mesh.visibility = 0;
    return mesh;
  }

  private startAtmosphere(run: AtmosphereRun): void {
    this.atmo = run;
    this.speed = 1;
    const c = run.profile.camera;
    const forward = forwardOf(run.subject.rotationQuaternion ?? Quaternion.Identity());
    const right = Vector3.Cross(forward, Vector3.Up()).normalize();
    this.camera.fov = c.fov;
    this.camera.position.copyFrom(run.origin.add(right.scale(c.right)).add(Vector3.Up().scale(c.up)).add(forward.scale(c.forward)));
    if (run.kind === 'arrive') {
      run.subject.position.copyFrom(run.origin.subtract(run.dir.scale(run.profile.portalDistance)));
      this.lookAt = run.subject.position.clone();
    } else {
      this.lookAt = run.origin.clone();
    }
    this.camera.setTarget(this.lookAt);
    this.onCue?.('open');
  }

  private updateAtmosphere(dt: number, run: AtmosphereRun): void {
    run.t += dt * this.speed;
    const p = run.profile;
    const u = clamp01(run.t / p.travelTime);
    let heat: number;
    if (run.kind === 'depart') {
      run.subject.position.copyFrom(run.origin.add(run.dir.scale(ease.in(u) * p.portalDistance)));
      heat = Math.pow(u, 1.3);
      if (u >= 1 && !run.burst) {
        run.burst = true;
        this.onCue?.('cross');
        const at = run.subject.position.clone();
        this.vfx.flash(at, [1, 0.75, 0.45], p.portalRadius * 4, 0.6, 1.6);
        this.vfx.light(at, [1, 0.5, 0.2], 6, p.portalRadius * 14, 0.7);
        run.subject.setEnabled(false);
      }
    } else {
      run.subject.position.copyFrom(run.origin.subtract(run.dir.scale((1 - ease.out(u)) * p.portalDistance)));
      heat = 1 - clamp01(u / 0.75);
      if (!run.burst) {
        run.burst = true;
        this.vfx.flash(run.subject.position.clone(), [1, 0.75, 0.45], p.portalRadius * 3, 0.5, 1.4);
      }
    }
    // Gaine de plasma autour de la coque, décalée vers l'avant du mouvement, scintillante ; étincelles ; secousse.
    const r = p.portalRadius;
    run.plasma.position.copyFrom(run.subject.position.add(run.dir.scale(r * 0.45)));
    run.plasma.rotationQuaternion = run.subject.rotationQuaternion?.clone() ?? Quaternion.Identity();
    run.plasma.visibility = run.subject.isEnabled() ? heat * (0.7 + 0.3 * Math.sin(run.t * 47)) : 0;
    run.sparks -= dt;
    if (heat > 0.15 && run.subject.isEnabled() && run.sparks <= 0) {
      run.sparks = 0.06;
      // Étincelles sur la face avant, réparties le long de la coque, filant vers l'arrière du mouvement.
      const along = forwardOf(run.subject.rotationQuaternion ?? Quaternion.Identity()).scale(((Math.random() - 0.5) * (p.length ?? r * 2)) * 0.8);
      this.vfx.sparks(run.subject.position.add(run.dir.scale(r * 1.2)).add(along), [1, 0.6, 0.25], 18, r * 3, 0.6, run.dir.negate());
    }
    const shake = heat * r * 0.015;
    const target = run.subject.isEnabled() ? run.subject.position : run.origin.add(run.dir.scale(p.portalDistance));
    this.lookAt = Vector3.Lerp(this.lookAt, target, Math.min(1, dt * 4)).add(new Vector3((Math.random() - 0.5) * shake, (Math.random() - 0.5) * shake, 0));
    this.camera.setTarget(this.lookAt);
    const end = run.kind === 'depart' ? p.travelTime + 0.35 : p.travelTime;
    if (run.t >= end) {
      if (run.kind === 'arrive') run.subject.position.copyFrom(run.origin);
      run.plasma.dispose();
      this.atmo = null;
      this.speed = 1;
      run.done();
    }
  }

  /** Départ : le sujet part de sa pose actuelle vers un portail ouvert devant lui. */
  depart(subject: TransformNode, profile: JumpProfile, done: () => void): void {
    const rotation = subject.rotationQuaternion?.clone() ?? Quaternion.Identity();
    const forward = forwardOf(rotation);
    const origin = subject.position.clone();
    const portal = this.openPortal(origin.add(forward.scale(profile.portalDistance)), rotation, profile);
    this.start({ kind: 'depart', subject, profile, origin, rotation, forward, portal, t: 0, crossed: false, done, ended: false });
  }

  /** Arrivée : le sujet surgit d'un portail derrière `rest` (sa pose finale, déjà celle du nœud). */
  arrive(subject: TransformNode, profile: JumpProfile, done: () => void): void {
    const rotation = subject.rotationQuaternion?.clone() ?? Quaternion.Identity();
    const forward = forwardOf(rotation);
    const origin = subject.position.clone();
    const portal = this.openPortal(origin.subtract(forward.scale(profile.portalDistance)), rotation, profile);
    subject.setEnabled(false);
    this.start({ kind: 'arrive', subject, profile, origin, rotation, forward, portal, t: 0, crossed: false, done, ended: false });
  }

  /** Accélère la fin de la séquence en cours (touche de menu). */
  skip(): void {
    this.speed = 5;
  }

  private start(run: Run): void {
    this.run = run;
    this.speed = 1;
    const c = run.profile.camera;
    const up = Vector3.Up();
    const right = Vector3.Cross(run.forward, up).normalize();
    this.camera.fov = c.fov;
    this.camera.position.copyFrom(run.origin.add(right.scale(c.right)).add(up.scale(c.up)).add(run.forward.scale(c.forward)));
    this.lookAt = run.kind === 'depart' ? Vector3.Lerp(run.origin, run.portal.root.position, 0.4) : run.portal.root.position.clone();
    this.camera.setTarget(this.lookAt);
  }

  update(dt: number): void {
    for (let i = this.portals.length - 1; i >= 0; i--) {
      const p = this.portals[i]!;
      p.age += dt * this.speed;
      const open = clamp01(p.age / p.profile.openTime);
      const close = clamp01((p.age - p.closeAt) / p.profile.closeTime);
      const scale = Math.max(0.001, ease.back(open) * (1 - ease.in(close)));
      p.root.scaling.setAll(scale);
      p.disc.rotation.z -= dt * this.speed * 1.6;
      p.disc.visibility = 0.85 + 0.15 * Math.sin(p.age * 9);
      if (close >= 1) {
        p.root.dispose();
        this.portals.splice(i, 1);
      }
    }
    if (this.atmo) this.updateAtmosphere(dt, this.atmo);
    const run = this.run;
    if (!run) return;
    run.t += dt * this.speed;
    if (run.kind === 'depart') this.updateDeparture(run);
    else this.updateArrival(run);
    // Caméra fixe qui suit le sujet en douceur ; au départ, cadrée entre le vaisseau et le portail.
    const portal = run.portal.root.position;
    const target = !run.subject.isEnabled() ? portal : run.kind === 'depart' ? Vector3.Lerp(run.subject.position, portal, 0.4) : run.subject.position;
    this.lookAt = Vector3.Lerp(this.lookAt, target, Math.min(1, dt * 4));
    this.camera.setTarget(this.lookAt);
    if (run.ended) {
      this.run = null;
      this.speed = 1;
      run.done();
    }
  }

  private updateDeparture(run: Run): void {
    const p = run.profile;
    const t0 = p.openTime * 0.6;
    const u = clamp01((run.t - t0) / p.travelTime);
    const s = ease.in(u);
    run.subject.position.copyFrom(run.origin.add(run.forward.scale(s * p.portalDistance)));
    // Étirement dans le dernier tiers du trajet.
    run.subject.scaling.set(1, 1, 1 + (p.stretch - 1) * clamp01((u - 0.66) / 0.34));
    if (u >= 1 && !run.crossed) {
      run.crossed = true;
      run.subject.setEnabled(false);
      run.subject.scaling.setAll(1);
      this.burst(run.portal.root.position, p.portalRadius);
      run.portal.closeAt = run.portal.age + 0.2;
    }
    if (run.crossed && run.t >= t0 + p.travelTime + 0.2 + p.closeTime) run.ended = true;
  }

  private updateArrival(run: Run): void {
    const p = run.profile;
    const t0 = p.openTime * 0.8;
    if (run.t < t0) return;
    if (!run.crossed) {
      run.crossed = true;
      run.subject.setEnabled(true);
      this.burst(run.portal.root.position, p.portalRadius);
    }
    const u = clamp01((run.t - t0) / p.travelTime);
    const s = ease.out(u);
    run.subject.position.copyFrom(run.origin.subtract(run.forward.scale((1 - s) * p.portalDistance)));
    run.subject.scaling.set(1, 1, 1 + (p.stretch - 1) * (1 - clamp01(u / 0.3)));
    if (u >= 0.45 && run.portal.closeAt === Infinity) run.portal.closeAt = run.portal.age;
    if (u >= 1) {
      run.subject.position.copyFrom(run.origin);
      run.subject.scaling.setAll(1);
      run.ended = true;
    }
  }

  /** Éclair, onde de choc, étincelles et lumière au passage du portail. */
  private burst(at: Vector3, radius: number): void {
    this.onCue?.('cross');
    const white: [number, number, number] = [0.85, 0.93, 1];
    this.vfx.flash(at.clone(), white, radius * 2.2, 0.5, 1.6);
    this.vfx.flash(at.clone(), PORTAL_COLOR, radius * 3.5, 0.9, 1.2);
    this.vfx.ring(at.clone(), PORTAL_COLOR, radius * 3, 0.8);
    this.vfx.sparks(at.clone(), white, 60, radius * 4, 0.9);
    this.vfx.light(at.clone(), PORTAL_COLOR, 6, radius * 12, 0.9);
  }

  private openPortal(position: Vector3, rotation: Quaternion, profile: JumpProfile): Portal {
    const r = profile.portalRadius;
    const root = new TransformNode('portal', this.scene);
    root.position.copyFrom(position);
    root.rotationQuaternion = rotation.clone();
    root.scaling.setAll(0.001);
    const ring = MeshBuilder.CreateTorus('portal-ring', { diameter: r * 2, thickness: r * 0.07, tessellation: 64 }, this.scene);
    ring.rotation.x = Math.PI / 2;
    ring.material = this.ringMaterial;
    ring.parent = root;
    ring.isPickable = false;
    const outer = MeshBuilder.CreateTorus('portal-ring-outer', { diameter: r * 2.35, thickness: r * 0.025, tessellation: 64 }, this.scene);
    outer.rotation.x = Math.PI / 2;
    outer.material = this.ringMaterial;
    outer.parent = root;
    outer.isPickable = false;
    const disc = MeshBuilder.CreateDisc('portal-disc', { radius: r * 1.02, tessellation: 64, sideOrientation: 2 }, this.scene);
    disc.material = this.discMaterial;
    disc.parent = root;
    disc.isPickable = false;
    const portal: Portal = { root, disc, age: 0, closeAt: Infinity, profile };
    this.onCue?.('open');
    this.portals.push(portal);
    this.vfx.flash(position.clone(), PORTAL_COLOR, r * 1.6, profile.openTime, 1.2);
    this.vfx.light(position.clone(), PORTAL_COLOR, 3, r * 10, profile.openTime + 0.5);
    return portal;
  }

  /** Abandon (retour au titre pendant une séquence) : portails retirés, sujet rendu visible. */
  clear(): void {
    if (this.atmo) {
      this.atmo.plasma.dispose();
      this.atmo.subject.setEnabled(true);
      this.atmo = null;
    }
    for (const p of this.portals) p.root.dispose();
    this.portals.length = 0;
    if (this.run) {
      this.run.subject.setEnabled(true);
      this.run.subject.scaling.setAll(1);
    }
    this.run = null;
  }
}

function forwardOf(rotation: Quaternion): Vector3 {
  return new Vector3(0, 0, -1).rotateByQuaternionToRef(rotation, new Vector3()).normalize();
}

/** Vortex : bras spiraux sur un fond radial, transparent au bord. */
function makeSwirlTexture(scene: Scene): DynamicTexture {
  const size = 512;
  const tex = new DynamicTexture('portal-swirl', { width: size, height: size }, scene, true);
  const ctx = tex.getContext() as unknown as CanvasRenderingContext2D;
  const c = size / 2;
  const base = ctx.createRadialGradient(c, c, 0, c, c, c);
  base.addColorStop(0, 'rgba(235,245,255,1)');
  base.addColorStop(0.18, 'rgba(150,200,255,0.9)');
  base.addColorStop(0.55, 'rgba(70,90,230,0.45)');
  base.addColorStop(0.85, 'rgba(120,60,220,0.25)');
  base.addColorStop(1, 'rgba(0,0,0,0)');
  ctx.fillStyle = base;
  ctx.fillRect(0, 0, size, size);
  const arms = 6;
  for (let a = 0; a < arms; a++) {
    for (let i = 0; i < 240; i++) {
      const k = i / 240;
      const angle = (a / arms) * Math.PI * 2 + k * Math.PI * 3.2;
      const radius = k * c * 0.95;
      const alpha = 0.35 * (1 - k) * Math.min(1, k * 6);
      ctx.fillStyle = `rgba(210,235,255,${alpha.toFixed(3)})`;
      ctx.beginPath();
      ctx.arc(c + Math.cos(angle) * radius, c + Math.sin(angle) * radius, 2 + k * 10, 0, Math.PI * 2);
      ctx.fill();
    }
  }
  tex.update();
  tex.hasAlpha = true;
  return tex;
}
